// ABOUTME: Full-screen in-app privacy policy overlay opened from the disclosure link.
// ABOUTME: Renders the PRIVACY_POLICY copy; sits above the consent gate and preferences panel.

import { useMicVoiceControl } from './micVoiceControl';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { PRIVACY_POLICY, policyFor } from './consentConfig';
import {
  RAIL_UNIT, u, uMin, CONSENT_PANEL, CONSENT_TEXT, PANEL_PAD, PANEL_RADIUS, PANEL_W, PANEL_MAX_VW,
} from '../components/ReelChrome/railGeometry';
import {
  MOBILE_UNIT, MOBILE_CONSENT, MOBILE_CONSENT_TEXT, MOBILE_PANEL, muMin, MIN_TEXT_PX,
} from '../components/ReelChrome/mobileGeometry';
import { useBreakpoint } from '../components/Projectionist/useViewportSize';

// The close affordance is the one control on this overlay, so it carries the 44px comfortable hit
// area explicitly: 88 authored px resolves to ~38px on desktop and ~23px on a 390px handset, both
// under the minimum. `uMin`/`muMin` grow the visual box rather than `.reel-touch-target`'s invisible
// pseudo-element, because at this size the glyph itself also wants to be bigger.
const CLOSE_BOX_AUTHORED = 88;
const CLOSE_BOX_MIN_PX = 44;
const CLOSE_GLYPH_AUTHORED = 40;
const CLOSE_GLYPH_MIN_PX = 20;

/**
 * The reel-chrome dressing of the policy overlay (Figma 113:6517's panel language).
 *
 * Same overlay behaviour as the generic variant below — Escape, backdrop click, close button, the
 * same z-[210] layer — only the surface changes: the frosted dark panel, the Gilroy type ramp and
 * the reel colour tokens that the rail's ConsentPanel and the pre-entry ConsentGate already use.
 * Kept as a separate branch (rather than a themed rewrite of the one component) for the same reason
 * ConsentGate keeps two: flag-off tenants must render byte-identical markup (SPEC-26 AC-9).
 */
function ReelPolicyPanel({ onClose, brandName, brandContact }) {
  const isMobile = useBreakpoint() === 'mobile';
  // The copy with the tenant's name filled in (controller / contact) — never a literal brand here.
  const policy = policyFor(brandName, brandContact, { micVoiceControl: useMicVoiceControl() }); // mic_voice_control: rights copy

  // Exactly the switch the rail's cart/wishlist/privacy panels and ConsentGate's reel branch make:
  // the desktop table under RAIL_UNIT, the mobile table under MOBILE_UNIT. Without it the panel
  // would sit at RAIL_UNIT's 0.43px floor — a fixed real-px size on every viewport below ~2064px,
  // i.e. every phone — and its width would be a Tailwind breakpoint unrelated to the rail's own.
  const unitCss = isMobile ? MOBILE_UNIT : RAIL_UNIT;
  const geometry = isMobile ? MOBILE_CONSENT : CONSENT_PANEL;
  const text = isMobile ? MOBILE_CONSENT_TEXT : CONSENT_TEXT;
  const panelPad = isMobile ? MOBILE_PANEL.pad : PANEL_PAD;
  const panelRadius = isMobile ? MOBILE_PANEL.radius : PANEL_RADIUS;
  const panelWidthCss = isMobile ? u(MOBILE_PANEL.w) : `min(${u(PANEL_W)}, ${PANEL_MAX_VW}vw)`;
  const unitMin = isMobile ? muMin : uMin;

  // This overlay is the only surface in the chrome that is a long read rather than a label or a
  // sentence, so unlike the consent rows it carries a real-px floor on its type. One floor, applied
  // proportionally: MIN_TEXT_PX is the floor for the smallest token (body copy) and every larger
  // token gets that same floor scaled by its own ratio to it — so the type hierarchy survives the
  // floor instead of collapsing into it, which is what a flat 11px on every token would do. On a
  // desktop viewport the floor never binds (26.667 authored px sets at 11.5 at RAIL_UNIT's own
  // floor), so this changes nothing there and only lifts the smallest handsets.
  const type = (authored) => unitMin(authored, (MIN_TEXT_PX * authored) / text.rowBody);

  // The mobile consent table sets its panel title and its row titles at the SAME token (37.333).
  // That is right for a panel whose title sits above three fixed rows, and flat for a document with
  // seven headings under it — rendered, the title stopped reading as the title. Desktop's own table
  // authors the step (49.917 over 37.333), so mobile borrows that ratio rather than a number
  // invented here, and desktop is left exactly as authored.
  const titleSize = isMobile
    ? text.title * (CONSENT_TEXT.title / CONSENT_TEXT.rowTitle)
    : text.title;

  const closeBox = unitMin(CLOSE_BOX_AUTHORED, CLOSE_BOX_MIN_PX);
  const closeGlyph = unitMin(CLOSE_GLYPH_AUTHORED, CLOSE_GLYPH_MIN_PX);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 16, scale: 0.98 }}
      transition={{ duration: 0.25 }}
      onClick={(e) => e.stopPropagation()}
      className="relative bg-reel-chrome-panel backdrop-blur-reel-chrome border border-white/10 shadow-2xl flex flex-col"
      style={{
        '--u': unitCss,
        width: panelWidthCss,
        maxWidth: '100%',
        // A percentage of the backdrop's own padded box (safe areas already subtracted) rather than
        // a bare vh/dvh: it is the one term that stays correct whatever the browser does with a
        // collapsing URL bar, while still leaving the modal visibly inset on a tall desktop window.
        maxHeight: '85%',
        borderRadius: u(panelRadius),
        padding: u(panelPad),
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close privacy policy"
        // Pinned to the panel, NOT to the scrolling content below it: the policy is long enough to
        // scroll on any viewport, and a close control that scrolls off the top leaves a guest on a
        // touch device with no visible way out until they reach the bottom. `top`/`right: 0` is the
        // panel's own border edge (the nearest positioned ancestor is the panel div itself). A
        // -50% offset centers the button ON that edge (half in, half out), which still read as
        // "touching" the panel — a full -100% offset clears the whole box outside the border,
        // tangent to the corner rather than straddling it.
        className="absolute z-10 flex items-center justify-center rounded-full bg-reel-chrome-iconChip text-reel-chrome-textPrimary hover:opacity-70 transition-opacity reel-touch-target"
        style={{
          top: `calc(${closeBox} * -1)`,
          // Outward by a full box wherever the room exists (desktop: a 446px panel in a 1280px+
          // viewport), but never past the viewport's edge. On a phone the panel is capped at the
          // backdrop's width, and the full-box offset put 28 of the button's 44px off-screen with only
          // the 16px padding strip left tappable (2026-09-03). `100%` is the panel's own width here,
          // so the second term is exactly the room between the panel's right edge and the viewport's.
          right: `calc(-1 * min(${closeBox}, (100vw - 100%) / 2))`,
          width: closeBox,
          height: closeBox,
        }}
      >
        <X aria-hidden="true" style={{ width: closeGlyph, height: closeGlyph }} />
      </button>

      {/* `min-h-0` is what lets this shrink below its content inside the flex column — without it a
          flex item's implicit `min-height: auto` keeps it at full content height, the panel grows
          past its own max-height and the footer below is pushed off the bottom of the screen. */}
      <div className="overflow-y-auto overscroll-contain min-h-0">
        <h2
          className="m-0 font-reel-heading font-extrabold text-reel-chrome-textPrimary"
          style={{ fontSize: type(titleSize) }}
        >
          {policy.title}
        </h2>

        {/* Version and draft notice. The generic variant tints the notice amber; there is no accent
            in the reel palette and amber-600 is close to unreadable on the dark panel, so the two
            are separated by weight of colour instead — dim for the version, primary for the notice
            that actually needs to be noticed. */}
        <div
          className="flex flex-wrap items-center font-reel-body uppercase tracking-widest"
          style={{ gap: u(24), marginTop: u(16), fontSize: type(text.rowBody) }}
        >
          <span className="text-reel-chrome-textDim">{policy.updated}</span>
        </div>

        <div className="flex flex-col" style={{ gap: u(40), marginTop: u(48) }}>
          {policy.sections.map((section) => (
            <section key={section.heading}>
              <h3
                className="m-0 font-reel-heading font-bold text-reel-chrome-textPrimary"
                style={{ fontSize: type(text.rowTitle) }}
              >
                {section.heading}
              </h3>
              <p
                className="m-0 font-reel-body text-reel-chrome-textDim leading-relaxed"
                style={{ fontSize: type(text.rowBody), marginTop: u(16) }}
              >
                {section.body}
              </p>
            </section>
          ))}
        </div>
      </div>

      {/* Outside the scroller, for the same reason the X above is: the policy is longer than any
          viewport, and rendered the first time round this pill was half-clipped by the fold — which
          reads as a broken button rather than as "scroll for more". Pinned, it is also the guest's
          one always-visible way out on a touch device. */}
      {/* The hairline is doing work, not decoration: with the CTA pinned, whatever section happens
          to be at the fold sits immediately above it, and rendered at handset width a half-cut
          heading touching the pill read as one crowded block. The rule ends the scroll area, and it
          reuses the panel's own border colour rather than introducing a token. */}
      <div
        className="shrink-0 border-t border-white/10"
        style={{ marginTop: u(geometry.footerGap), paddingTop: u(geometry.footerGap) }}
      >
        {/* The light CTA (`primaryBg`), same as the rail panel's CANCEL and the gate's Enter
            Boutique: this is a dismissal, and it is the only action on the overlay. */}
        <button
          type="button"
          onClick={onClose}
          className="w-full uppercase bg-reel-cta-primaryBg text-reel-chrome-textInk font-reel-body flex items-center justify-center text-center leading-tight hover:opacity-90 transition-opacity"
          style={{
            minHeight: u(geometry.footerH), borderRadius: u(geometry.rowRadius), fontSize: type(text.cta),
            paddingTop: u(16), paddingBottom: u(16), paddingLeft: u(24), paddingRight: u(24),
          }}
        >
          Close
        </button>
      </div>
    </motion.div>
  );
}

/** The pre-SPEC-26 white modal. Unchanged — this is what flag-off tenants still render. */
function GenericPolicyPanel({ onClose, brandName, brandContact }) {
  const policy = policyFor(brandName, brandContact, { micVoiceControl: useMicVoiceControl() }); // mic_voice_control: rights copy
  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 16, scale: 0.98 }}
      transition={{ duration: 0.25 }}
      onClick={(e) => e.stopPropagation()}
      className="relative bg-white/95 backdrop-blur rounded-[2rem] border border-black/10 shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-8"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close privacy policy"
        className="absolute top-5 right-5 p-2 rounded-full hover:bg-black/5 transition-colors"
      >
        <X size={16} />
      </button>

      <h2 className="text-lg tracking-widest uppercase font-semibold pr-8">{policy.title}</h2>
      <div className="flex items-center gap-3 mt-2 mb-6">
        <span className="text-[10px] uppercase tracking-widest text-neutral-400">{policy.updated}</span>
      </div>

      <div className="space-y-5">
        {policy.sections.map((section) => (
          <section key={section.heading}>
            <h3 className="text-sm font-medium tracking-wide mb-1">{section.heading}</h3>
            <p className="text-sm text-neutral-600 leading-relaxed">{section.body}</p>
          </section>
        ))}
      </div>

      <div className="mt-8">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-3 rounded-full border border-black bg-black text-white hover:bg-neutral-800 transition-colors tracking-widest uppercase text-xs font-medium"
        >
          Close
        </button>
      </div>
    </motion.div>
  );
}

/**
 * @param {{ open: boolean, onClose: () => void, reelChrome?: boolean, brandName?: string, brandContact?: string }} props
 *
 * `reelChrome` swaps the generic white modal for the dark reel-chrome panel, exactly as it does on
 * ConsentGate, and changes nothing else: same overlay presentation, same dismissal contract.
 * `brandName` and `brandContact` (from /api/config: `brand_name`, `privacy_contact`) fill the policy's
 * "who is responsible" and contact lines; absent, the copy reads "the house" / a neutral address phrase.
 */
export function PrivacyPolicy({ open, onClose, reelChrome = false, brandName = '', brandContact = '' }) {
  // Read-only overlay: dismissible via Escape (in addition to the close button/backdrop).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Portaled to `document.body` rather than rendered inline: the reel footer/mic bar
  // (ReelFooter/MobileFooter) mounts through OverlayPortal, its own `createPortal` straight onto
  // `document.body` with `position: fixed; z-index: 65`. The app root div that this component
  // would otherwise render inside has no `position`/`z-index` of its own, so per CSS stacking
  // rules its entire subtree — including this panel's `z-[210]` — paints below any positioned,
  // explicitly-z-indexed sibling of that root, i.e. below the footer, no matter how high z-[210]
  // reads locally. Portaling here puts the panel in the same stacking context as the footer's
  // portal so the two z-indices are actually compared, which is what fixed the bar drawing on
  // top of the policy panel on both desktop and mobile.
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="privacy-policy"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label={PRIVACY_POLICY.title}
          onClick={onClose}
          // z-[210] is deliberate and unchanged: above the consent gate (200), the full-screen
          // try-on film (180) and every piece of reel chrome, and below only the orientation lock
          // (300), which is meant to be the one thing that can cover it. That comparison only
          // holds because this whole panel is now portaled to document.body alongside the reel
          // footer's own portal (see the createPortal call above) — otherwise z-[210] would be
          // compared against nothing and lose to any positioned sibling of the (unpositioned)
          // app root, which is what let the footer/mic bar draw on top of it before.
          className={`fixed inset-0 z-[210] bg-black/60 backdrop-blur-md flex items-center justify-center pointer-events-auto${reelChrome ? '' : ' p-4 font-sans text-neutral-900'}`}
          style={reelChrome ? {
            // Safe-area insets on the backdrop rather than the panel, so the panel's own percentage
            // max-height already has the notch and home indicator subtracted from it.
            paddingTop: 'calc(1rem + var(--reel-safe-t))',
            paddingBottom: 'calc(1rem + var(--reel-safe-b))',
            paddingLeft: 'calc(1rem + var(--reel-safe-l))',
            paddingRight: 'calc(1rem + var(--reel-safe-r))',
          } : undefined}
        >
          {reelChrome
            ? <ReelPolicyPanel onClose={onClose} brandName={brandName} brandContact={brandContact} />
            : <GenericPolicyPanel onClose={onClose} brandName={brandName} brandContact={brandContact} />}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
